/**
 * General-agent (OrionIQ "IQon") chat transport types. Shared so both OrionIQ and Open 360 call the
 * same general-agent API (POST /orioniq-be/internal/ai-agent/General + GET /orioniq-be/agent-runs/session/:id)
 * instead of the legacy bedrock-ai-sse chat. Snake_case fields mirror the wire/API shape.
 */
import {
  getResponseMode,
  type ChatMode,
  type ChatOrigin,
  type ChatSourceRef,
  type RunIncompleteReason,
} from '@logz-pkg/models';
import type { IAgentRun, RunStatus } from './types';

/** Per-model cumulative token counters within a single agent run. */
export interface IModelTokenUsage {
  cumulative_input_tokens?: number;
  cumulative_output_tokens?: number;
  cumulative_total_tokens?: number;
}

export interface IFunctionCallItem {
  name?: string;
  function?: string;
  input?: Record<string, unknown>;
  // `value` is usually `string | string[]`, but the wire also sends objects (e.g. an `execute_api_read`
  // `headers` param: `{ Authorization: '…' }`) and object-arrays (e.g. a `write_todos` `todos` param:
  // `[{ content, status }]`). The consumer adapter coerces it to the block's `string | string[]`
  // (JSON-stringifying objects + object-array elements) — see chat-content-to-blocks.util `stringifyAndRedact`.
  parameters?: Array<{ name?: string; type?: string; value?: string | string[] | Record<string, unknown> | unknown[] }>;
}

/** Intermediate step emitted by the agent during a run (rational, tool call, or special event). */
export interface IChatContentItem {
  rational?: string;
  function_calls?: IFunctionCallItem[];
  type?: string;
  subagent?: string | null;
  description?: string;
  step?: number;
  text?: string;
  error?: string;
  failure?: string;
  timestamp?: Date | string;
  usage_metadata?: Record<string, IModelTokenUsage>;
  elapsed_ms?: number;
  sub_session_id?: string;
  // A delegation's structured closing conclusion (the deepagent's final `ai` step). Shape = `AgentVerdict`
  // (3 profile variants, no type tag); typed `unknown` here and validated/narrowed by the adapter
  // (`chat-content-to-blocks` → a `verdict` block). Absent on plain chat / error / limit turns.
  agent_output?: unknown;
  // ORIONIQ-967/1018 tool-sources handshake — a datasource tool's `type:'tool'` event carries a structured
  // `source` descriptor (null for non-datasource tools) and a `success` flag (true = data incl. zero rows,
  // false = recoverable error the query hit, null = non-datasource tool / hard-raise). The adapter copies
  // both onto the paired tool-call block; the host builds the deep-link from `source`.
  source?: ChatSourceRef | null;
  success?: boolean | null;
}

/** GET /orioniq-be/agent-runs/session/:sessionId — agent run plus polling completion flag + transcript steps. */
export type IAgentRunSessionResponse = Partial<IAgentRun> & {
  isDone: boolean;
  title: string;
  chatContent?: IChatContentItem[];
  // Whether the account's free allowance still covered this run, as orioniq-be's cap gate resolved it at run
  // time. Free runs are excluded from the charged-token total shown beside the billing notice, so a
  // conversation that starts on the free tier and crosses over counts from the crossing.
  //
  // NULL/undefined means UNKNOWN, never "not free": orioniq-be only resolves it when the account has a
  // configured monthly budget, and older runs predate the column. An unknown run is COUNTED — dropping it
  // would understate the figure the user is shown.
  underFreeUsage?: boolean | null;
  // The next question the user might ask, written in their own voice by the AI service after the answer
  // (ORIONIQ-1480, contract 3.8.0). Offered in the composer; accepting it fills the draft, it never sends.
  //
  // NULL/ABSENT IS THE NORMAL CASE and carries no meaning: the service declines whenever a follow-up isn't
  // worth offering, and generation is best-effort. Never render a loading state waiting for it.
  //
  // Present on THIS endpoint only — GET /conversations/:sessionId deliberately omits it, so a reloaded
  // transcript does not re-offer a stale suggestion. This type is hand-maintained in two copies (the other
  // is Artemis' packages/frontend-services); nothing enforces that they agree.
  nextStepSuggestion?: string | null;
};

/** Wire format for an image attachment on POST /orioniq-be/internal/ai-agent/General (snake_case per API). */
export interface IOiqBotImagePayload {
  data: string;
  media_type: string;
}

export interface IOiqBotPrincipal {
  accountId: string;
}

export interface IGeneralAgentChatRequest {
  message: string;
  sessionId: string;
  images?: IOiqBotImagePayload[];
  principal?: IOiqBotPrincipal;
  // ORIONIQ-1216: app-directed context — Explore page/account/attached blocks — sent OUT of `message` (which
  // is now the user's verbatim text). orioniq-be routes this into the `/oiq-bot` request's
  // `context.systemReminder` (contract 1.1.0), appending its own naming-ack; the AI Service wraps it verbatim
  // in a single `<system-reminder>` block. Omitted when there's nothing to send.
  //
  // The `<response_mode>` directive NO LONGER rides here (ORIONIQ-1475) — send `mode` below and orioniq-be
  // composes the directive from its registry.
  systemReminder?: string;
  // The chat effort mode for THIS turn (ORIONIQ-1475). Structural, not prose: orioniq-be resolves it (request →
  // the user's stored preference → the registry default) and builds the `<response_mode>` directive itself,
  // so the wording lives in one place instead of being forked per client. Omit to accept the user's stored
  // preference — which is what a caller with no mode picker should do.
  mode?: ChatMode;
  // Which surface sent this turn (ORIONIQ-1475). Persisted per-run and aggregated as a conversation's
  // `startedOn` / `lastUsedOn`. Set by the HOST, not here: this service is the one send site for both web
  // surfaces, so a value set here would make them indistinguishable — the exact thing it exists to record.
  origin?: ChatOrigin;
}

/** Immediate response from POST /orioniq-be/internal/ai-agent/General (starts an async agent run). */
export interface IGeneralAgentChatPostResponse {
  status: string;
  sessionId?: string;
  tabSessionId?: string;
  message?: string;
}

/**
 * Session-grouped chat history (ORIONIQ-1053). A "conversation" = all general-agent runs sharing a sessionId;
 * the resource id IS the sessionId. Served by orioniq-be's `/conversations` collection (user-scoped).
 */

/** GET /orioniq-be/conversations — one row per session (general-agent only, newest-run title, last activity). */
export interface IConversationListItem {
  sessionId: string;
  title: string | null;
  // The session's latest-run status, point-in-time (resolved on each list). The consumer derives its running
  // indicator from it.
  status: RunStatus | null;
  lastActivity: string;
  // User-turn count (runs) for the session (ORIONIQ-1131) — list-derived, so a consumer shows it without a
  // transcript fetch. Optional: absent until orioniq-be ships the projection (the two workspaces deploy separately).
  messageCount?: number;
  // Where the session's OLDEST and NEWEST run came from (ORIONIQ-1475). A session is not single-origin — start
  // a chat in the browser, continue it on the phone — so both are carried; they coincide for almost every
  // conversation and the ones where they differ are the interesting ones. Null for runs predating the column.
  startedOn?: ChatOrigin | null;
  lastUsedOn?: ChatOrigin | null;
}

export interface IConversationListResponse {
  results: IConversationListItem[];
}

/** One run within a session transcript: the user's message + the agent's steps + terminal answer. */
export interface IConversationRun {
  runId: string;
  createdAt: string;
  status: string;
  userMessage: string;
  // The agent-directed context this run was sent with (ORIONIQ-1216, contract 1.1.0).
  systemReminder?: string;
  chatContent?: IChatContentItem[];
  agentOutput: string | null;
  // Failsafe reason when the run ended involuntarily (null otherwise). [ORIONIQ-1182]
  incompleteReason?: RunIncompleteReason | null;
  // Whether the free allowance covered this run — excluded from the charged-token total. Null/undefined =
  // UNKNOWN and therefore counted (see IAgentRunSessionResponse).
  underFreeUsage?: boolean | null;
  // The mode this run actually ran in (ORIONIQ-1475) — read this instead of sniffing `<response_mode>` out of
  // the reminder. orioniq-be serves it from the run's column and falls back to parsing the stored reminder for
  // runs predating that column, so it is never null: optional ONLY for the window where orioniq-be has not yet
  // shipped the projection (the two workspaces deploy separately), like `messageCount` above.
  mode?: ChatMode;
  // Which surface sent this run (ORIONIQ-1475). Null for runs predating the column.
  origin?: ChatOrigin | null;
  // The follow-up this run suggested, or null (ORIONIQ-1480 follow-up). Every run of the transcript carries its
  // own; only the NEWEST is ever offered again. OPTIONAL for the deploy window: a server predating the field
  // omits the key, which reads as "not recorded" rather than "this run offered nothing" (null).
  //
  // The twin of Artemis' `packages/frontend-services` copy, with nothing enforcing that they agree.
  nextStepSuggestion?: string | null;
}

/**
 * What mode a persisted run actually ran in.
 *
 * `run.mode` is the answer whenever orioniq-be supplies it (ORIONIQ-1475) — it comes from the run's own column,
 * and orioniq-be derives it for rows predating that column, so it is right for old conversations too. The prose
 * sniff survives only for the window where orioniq-be has not yet shipped the field; the two workspaces deploy
 * separately.
 *
 * Reading the reminder BEFORE the message matters for the sniff: ORIONIQ-1216 moved the directive out of
 * `message` into `systemReminder`, so a post-1216 fast run whose message is clean reads as Advanced if you
 * only look at the message. That exact bug shipped in the Artemis twin of this file.
 *
 * A runtime export in a `.types.ts` file bends the repo convention, deliberately: this module is kept
 * byte-aligned with its Artemis twin by copy, and splitting the function out HERE but not THERE is how the
 * two copies drifted into disagreeing about that very bug. Keep it here until both repos move it together.
 */
export const resolveRunMode = (run: IConversationRun): ChatMode =>
  run.mode ?? getResponseMode(run.systemReminder ?? run.userMessage);

/** GET /orioniq-be/conversations/:sessionId — the full multi-run transcript for one session. */
export interface IConversationTranscriptResponse {
  id: string;
  title: string;
  runs: IConversationRun[];
}

/**
 * The LEGACY AI Assistant history (ORIONIQ-1307) — the pre-OrionIQ Open 360 assistant's conversations, served
 * read-only from the separate `/orioniq-be/conversations/legacy` collection. A conversation id is its own row id (NOT a
 * sessionId): the two products share no engine, so these can be read but never resumed, rated or deleted.
 *
 * Its own collection on purpose — legacy rows self-purge on the 60-day retention job, so this whole surface (types,
 * endpoints, adapter branch) is deletable in one PR once it drains.
 */

/** GET /orioniq-be/conversations/legacy — one row per legacy conversation. No `status`: it is never running. */
export interface ILegacyConversationListItem {
  id: string;
  title: string;
  lastActivity: string;
  // Question count, so the row shows a message count without fetching the transcript. orioniq-be only lists
  // conversations that have at least one, so this is always ≥ 1.
  messageCount: number;
}

export interface ILegacyConversationListResponse {
  results: ILegacyConversationListItem[];
}

/**
 * GET /orioniq-be/conversations/legacy/:convId — the transcript, projected onto the SAME run envelope the OrionIQ
 * transcript uses so the FE rebuilds it with the same `buildRunMessage` primitive. Each run is one
 * question-and-answer pair with an empty `chatContent` (the legacy trace steps are discarded), so it renders as a
 * plain transcript with no process view. `agentOutput: null` on a `status: 'Incomplete'` run = a question that never
 * got an answer.
 */
export type ILegacyConversationTranscriptResponse = IConversationTranscriptResponse;

/**
 * The chat-mode catalog (ORIONIQ-1475). `GET /orioniq-be/chat-modes` serves what modes exist and how to present
 * them, so a mode added to orioniq-be's registry appears in this picker with no client release — which is the
 * whole reason the copy is not a constant in this file any more.
 *
 * The `<response_mode>` directive is deliberately NOT part of this shape: it is instructions to the agent,
 * and a client that received it could edit it and send it back as page context.
 */
export interface IChatModeOptionResponse {
  value: ChatMode;
  label: string;
  description: string;
  /**
   * An icon name from the design system. A client MUST fall back when it does not recognise one — a mode
   * added after this bundle shipped can name an icon this bundle does not have.
   */
  icon: string;
  /** Exactly one entry carries it: the mode a user with no stored preference starts on. */
  isDefault?: boolean;
}

export interface IChatModesResponse {
  modes: IChatModeOptionResponse[];
}

/** The four notification toggles on the shared preferences document. Mobile owns the UI for these. */
export interface INotificationPreferences {
  chats: boolean;
  agents: boolean;
  mine: boolean;
  favorites: boolean;
}

/**
 * `GET|PUT /orioniq-be/user/preferences` (ORIONIQ-1463) — ONE per-user, per-account document shared by every
 * OrionIQ surface. The web only reads and writes `chatMode`; `notifications` and `starredAgentIds` belong to
 * the mobile app, and are typed here so a web write cannot accidentally omit-and-erase them (it cannot
 * anyway — PUT merges — but the shape should not pretend they do not exist).
 */
export interface IUserPreferences {
  chatMode: ChatMode;
  notifications: INotificationPreferences;
  starredAgentIds: string[];
}

/** Only what changed. orioniq-be merges, so a partial patch is the intended usage, not a shortcut. */
export interface IUserPreferencesPatch {
  chatMode?: ChatMode;
  notifications?: Partial<INotificationPreferences>;
  starredAgentIds?: string[];
}
